import * as THREE from 'three';
import type { Quat, Vec3 } from '../../sim/mathx';
import type { TelemetrySnapshot } from '../../state/telemetryBuffer';

export type ScoutRenderTelemetry = Pick<
  TelemetrySnapshot['scout'],
  'motorRpm' | 'flightController'
>;

const CRASH_BLINK_PERIOD_SECONDS = 0.15;
const ARMED_LED_COLOR = 0x00ff00;
const DISARMED_LED_COLOR = 0xff0000;

/**
 * The whole render scene is Z-up: Three (x, y, z) = ENU (east, north, up).
 * Scout body axes also match Three directly: FLU = (+x, +y, +z). This is why
 * an interpolated body-to-ENU quaternion can be copied without a basis change.
 */
export const setThreePositionFromEnu = (object: THREE.Object3D, position: Vec3): void => {
  object.position.set(position.x, position.y, position.z);
};

export const setThreeQuaternionFromEnuFlu = (object: THREE.Object3D, attitude: Quat): void => {
  object.quaternion.set(attitude.x, attitude.y, attitude.z, attitude.w).normalize();
};

const horizontalCylinder = (
  radius: number,
  length: number,
  material: THREE.Material,
  angle: number,
): THREE.Mesh => {
  const arm = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 8), material);
  arm.rotation.z = angle;
  arm.castShadow = true;
  return arm;
};

const disposeGroupResources = (group: THREE.Object3D): void => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of meshMaterials) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
};

/** Procedural scout quadrotor driven only by interpolated pose and telemetry. */
export class ScoutView extends THREE.Group {
  readonly propDiscs: readonly THREE.Mesh[];
  readonly led: THREE.Mesh;
  readonly ledMaterial: THREE.MeshStandardMaterial;

  private crashElapsedSeconds = 0;
  private disposed = false;

  constructor() {
    super();
    this.name = 'scout';

    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0x303943,
      roughness: 0.55,
      metalness: 0.25,
    });
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.34, 0.18), bodyMaterial);
    body.name = 'scout:body';
    body.castShadow = true;
    this.add(body);

    const armMaterial = new THREE.MeshStandardMaterial({
      color: 0x606b75,
      roughness: 0.65,
      metalness: 0.2,
    });
    const armLength = 0.72;
    const armOffsets: ReadonlyArray<readonly [number, number]> = [
      [0.255, 0.255],
      [0.255, -0.255],
      [-0.255, -0.255],
      [-0.255, 0.255],
    ];
    armOffsets.forEach(([x, y], index) => {
      // CylinderGeometry points along local Y. This angle aligns it from the
      // body origin to the motor center in the XY (east/north) plane.
      const angle = Math.atan2(-x, y);
      const arm = horizontalCylinder(0.025, armLength / 2, armMaterial, angle);
      arm.name = `scout:arm:${index}`;
      arm.position.set(x / 2, y / 2, 0);
      this.add(arm);
    });

    const propMaterial = new THREE.MeshBasicMaterial({
      color: 0xa9c5d1,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const props = armOffsets.map(([x, y], index) => {
      const prop = new THREE.Mesh(new THREE.CircleGeometry(0.18, 20), propMaterial);
      prop.name = `scout:prop:${index}`;
      prop.position.set(x, y, 0.08);
      this.add(prop);
      return prop;
    });
    this.propDiscs = props;

    this.ledMaterial = new THREE.MeshStandardMaterial({
      color: DISARMED_LED_COLOR,
      emissive: DISARMED_LED_COLOR,
      emissiveIntensity: 1,
      roughness: 0.25,
    });
    this.led = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 6), this.ledMaterial);
    this.led.name = 'scout:arm-led';
    this.led.position.set(-0.29, 0, 0.04);
    this.add(this.led);
  }

  update(
    interp: { posEnu: Vec3; attitude: Quat },
    telem: ScoutRenderTelemetry,
    dtSeconds: number,
  ): void {
    setThreePositionFromEnu(this, interp.posEnu);
    setThreeQuaternionFromEnuFlu(this, interp.attitude);

    const dt = Number.isFinite(dtSeconds) && dtSeconds > 0 ? dtSeconds : 0;
    for (let index = 0; index < this.propDiscs.length; index += 1) {
      const rpm = telem.motorRpm[index] ?? 0;
      this.propDiscs[index]!.rotation.z += Math.max(0, rpm) * dt;
    }

    const ledColor = telem.flightController.armed ? ARMED_LED_COLOR : DISARMED_LED_COLOR;
    this.ledMaterial.color.setHex(ledColor);
    this.ledMaterial.emissive.setHex(ledColor);

    if (telem.flightController.crashed) {
      this.crashElapsedSeconds += dt;
      const lit = Math.floor(this.crashElapsedSeconds / CRASH_BLINK_PERIOD_SECONDS) % 2 === 0;
      this.led.visible = lit;
      this.ledMaterial.emissiveIntensity = lit ? 1 : 0;
    } else {
      this.crashElapsedSeconds = 0;
      this.led.visible = true;
      this.ledMaterial.emissiveIntensity = 1;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    disposeGroupResources(this);
    this.clear();
  }
}
